import React from 'react';
import { motion } from 'framer-motion';
import { Leaf, Brain, Heart, Sunrise, Moon, Activity } from 'lucide-react';

export function FeaturesSection() {
  const wellnessBenefits = [
    {
      icon: Brain,
      title: 'Mental Clarity',
      description: 'Release stress and anxiety through guided meditation, mindfulness practices, and digital detox in serene mountain environments.',
      color: 'from-blue-500 to-cyan-500',
    },
    {
      icon: Heart,
      title: 'Emotional Balance',
      description: 'Reconnect with your inner self through therapeutic nature walks, journaling sessions, and heart-centered healing practices.',
      color: 'from-pink-500 to-rose-500',
    },
    {
      icon: Activity,
      title: 'Physical Renewal',
      description: 'Revitalize your body with yoga, forest bathing, mountain trekking, and holistic spa treatments using natural ingredients.',
      color: 'from-emerald-500 to-green-500',
    },
    {
      icon: Sunrise,
      title: 'Spiritual Growth',
      description: 'Deepen your spiritual connection through sunrise meditation, sacred mountain ceremonies, and contemplative practices.',
      color: 'from-amber-500 to-orange-500',
    },
    {
      icon: Leaf,
      title: 'Nature Healing',
      description: 'Experience the therapeutic power of pristine forests, mountain air, and natural hot springs for complete rejuvenation.',
      color: 'from-teal-500 to-emerald-500',
    },
    {
      icon: Moon,
      title: 'Deep Rest',
      description: 'Restore your energy with sound healing, restorative yoga, sleep therapy, and peaceful nights under mountain stars.',
      color: 'from-indigo-500 to-purple-500',
    },
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-white via-emerald-50/30 to-teal-50/30 relative overflow-hidden">
      {/* Background decoration */}
      <motion.div
        className="absolute top-0 right-0 w-96 h-96 bg-emerald-200/20 rounded-full blur-3xl"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
            className="inline-block px-6 py-3 bg-gradient-to-r from-emerald-100 to-teal-100 text-emerald-800 rounded-full mb-6"
          >
            Transformational Benefits
          </motion.div>
          <h2 className="mb-4 bg-gradient-to-r from-emerald-700 to-teal-700 bg-clip-text text-transparent">
            How Our Programs Transform You
          </h2>
          <p className="text-gray-700 max-w-3xl mx-auto text-lg">
            Experience holistic healing that addresses every aspect of your well-being. 
            Our mountain wellness programs are designed to bring profound and lasting transformation.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {wellnessBenefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ y: -8, scale: 1.02 }}
              className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500"
            >
              {/* Icon */}
              <motion.div
                className={`w-16 h-16 bg-gradient-to-br ${benefit.color} rounded-2xl flex items-center justify-center mb-6 shadow-lg`}
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.6 }}
              >
                <benefit.icon className="w-8 h-8 text-white" />
              </motion.div>

              {/* Content */}
              <h3 className="mb-3 text-gray-900">{benefit.title}</h3>
              <p className="text-gray-600 leading-relaxed">{benefit.description}</p>

              {/* Decorative element */}
              <motion.div
                className={`absolute bottom-0 left-0 h-1 bg-gradient-to-r ${benefit.color} rounded-full`}
                initial={{ width: 0 }}
                whileInView={{ width: '100%' }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 + 0.3, duration: 0.6 }}
              />
            </motion.div>
          ))}
        </div>

        {/* Additional healing promise */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="mt-16 text-center bg-gradient-to-r from-emerald-600 to-teal-600 rounded-3xl p-12 text-white"
        >
          <h3 className="text-white mb-4">Our Healing Promise</h3>
          <p className="text-emerald-50 text-lg max-w-3xl mx-auto leading-relaxed">
            Every program is carefully crafted by expert wellness practitioners who understand 
            the profound healing that occurs when ancient wisdom meets modern science in nature's sanctuary. 
            We don't just offer retreats—we facilitate life-changing transformations.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
